import { getP } from 'redux-polyglot'
import cartDeviceThumb from '../img/cart-device-thumb.jpg'
import api from '../libs/api'
import { getCartReference } from '../selectors/cart'

/**
 * Actions for retrieving cart details and items
 */
export const FETCH_CART_REQUEST = 'CART/FETCH_CART_REQUEST'

const fetchCartRequest = () => ({
  type: FETCH_CART_REQUEST
})

export const loadCart = () => dispatch => {
  dispatch(fetchCartRequest())
  return Promise.all([
    dispatch(loadCartDetails()),
    dispatch(loadCartItems())
  ])
}

/**
 * Actions for retrieving cart details
 */
export const FETCH_CART_DETAILS_REQUEST = 'CART/FETCH_CART_DETAILS_REQUEST'
export const FETCH_CART_DETAILS_SUCCESS = 'CART/FETCH_CART_DETAILS_SUCCESS'
export const FETCH_CART_DETAILS_FAILURE = 'CART/FETCH_CART_DETAILS_FAILURE'

const fetchCartDetailsRequest = () => ({
  type: FETCH_CART_DETAILS_REQUEST
})

export const fetchCartDetailsSuccess = (data) => {

  const payload = {
    total: data.price ? parseFloat(data.price) : 0,
    payNow: data.instant ? parseFloat(data.instant) : 0
  }

  if (data.free_trial_selected === 'yes') {
    payload.freeTrial = true
    payload.freeTrialType = data.free_trial_period_type
    payload.freeTrialLength = parseInt(data.free_trial_period_length, 10)
  }

  return {
    type: FETCH_CART_DETAILS_SUCCESS,
    payload
  }
}

const fetchCartDetailsFailure = (error) => ({
  type: FETCH_CART_DETAILS_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true
})

const loadCartDetails = () => (dispatch, getState) => {
  dispatch(fetchCartDetailsRequest())
  
  const cartReference = getCartReference(getState())
  return api.getCartDetails(cartReference)
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          return dispatch(fetchCartDetailsSuccess(response.data))
        } else {
          return dispatch(fetchCartDetailsFailure(response.reason))
        }
      },
      () => dispatch(fetchCartDetailsFailure(getP(getState()).t("checkout_app.error_loading_cart_details", {_:"An error has occurred while loading cart details"})))
    )
}

/**
 * Actions for retrieving cart items
 */
export const FETCH_CART_ITEMS_REQUEST = 'CART/FETCH_CART_ITEMS_REQUEST'
export const FETCH_CART_ITEMS_SUCCESS = 'CART/FETCH_CART_ITEMS_SUCCESS'
export const FETCH_CART_ITEMS_FAILURE = 'CART/FETCH_CART_ITEMS_FAILURE'

const fetchCartItemsRequest = () => ({
  type: FETCH_CART_ITEMS_REQUEST
})

const fetchCartItemsSuccess = cartItems => {
  let items = []
  cartItems.forEach((item) => {
    switch (item.type) {
      case 'channel_group':
        items.push({
          id: parseInt(item.id, 10),
          name: item.name,
          thumb: item.thumbnail,
          description: item.description,
          price: parseFloat(item.price),
          payNow: parseFloat(item.instant),
          itemId: parseInt(item.item_id, 10),
          itemType: item.type
        })
        break
      case 'premium':
        items.push({
          id: parseInt(item.id, 10),
          name: item.name,
          thumb: item.thumbnail,
          description: item.description,
          price: parseFloat(item.price),
          payNow: parseFloat(item.instant),
          itemId: parseInt(item.item_id, 10),
          itemType: item.type
        })
        break
      case 'addon_device':
        items.push({
          id: parseInt(item.id, 10),
          name: item.name,
          thumb: cartDeviceThumb,
          description: item.description,
          price: parseFloat(item.price),
          payNow: parseFloat(item.instant),
          itemId: parseInt(item.item_id, 10),
          itemType: "device"
        })
        break
      default:
    }
  })
  return {
    type: FETCH_CART_ITEMS_SUCCESS,
    payload: {
      items
    }
  }
}

const fetchCartItemsFailure = (error) => ({
  type: FETCH_CART_ITEMS_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true
})

const loadCartItems = () => (dispatch, getState) => {
  dispatch(fetchCartItemsRequest())
  
  const cartReference = getCartReference(getState())
  return api.getCartItems(cartReference)
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          return dispatch(fetchCartItemsSuccess(response.data.map(item => {
            switch (item.type) {
              case 'channel_group':
                return {
                  ...item,
                  description: item.is_recurring === 'yes' ? getP(getState()).t("checkout_app.recurring_channel_group_subscription", {_:"Recurring Channel Group Subscription"}) : getP(getState()).t("checkout_app.channel_group_subscription", {_:"Channel Group Subscription"})
                }
              case 'premium':
                return {
                  ...item,
                  description: item.is_recurring === 'yes' ? getP(getState()).t("checkout_app.recurring_premium_channel_subscription", {_:"Recurring Premium Channel Subscription"}) : getP(getState()).t("checkout_app.premium_channel_subscription", {_:"Premium Channel Subscription"})
                }
              case 'addon_device':
                return {
                  ...item,
                  description: getP(getState()).t("checkout_app.addon_device_subscription_package", {_:"Add-on device subscription package"})
                }
              default:
                return item
            }
          })))
        } else {
          return dispatch(fetchCartItemsFailure(response.reason))
        }
      },
      () => dispatch(fetchCartItemsFailure(getP(getState()).t("checkout_app.error_loading_cart_items", {_:"An error has occurred while loading cart items."})))
    )
}

/**
 * Actions for removing an item from cart
 */
export const REMOVE_ITEM_REQUEST = 'CART/REMOVE_ITEM_REQUEST'
export const REMOVE_ITEM_SUCCESS = 'CART/REMOVE_ITEM_SUCCESS'
export const REMOVE_ITEM_FAILURE = 'CART/REMOVE_ITEM_FAILURE'

const removeItemRequest = id => ({
  type: REMOVE_ITEM_REQUEST,
  payload: {
    id
  }
})

const removeItemFailure = error => ({
  type: REMOVE_ITEM_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true
})

export const removeItem = id => (dispatch, getState) => {
  dispatch(removeItemRequest(id))

  const cartReference = getCartReference(getState())
  return api.removeCartItem(cartReference, id)
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          return dispatch(loadCart())
        } else {
          return dispatch(removeItemFailure(response.reason))
        }
      },
      () => dispatch(removeItemFailure(getP(getState()).t("checkout_app.error_removing_item", {_:"An error has occurred while removing the item."})))
    )
}

/**
 * Actions for navigating through cart items
 */
export const FOCUS_POSITION = 'CART/FOCUS_POSITION'

export const focusPosition = (position) => ({
  type: FOCUS_POSITION,
  payload: {
    position
  }
})

/**
 * Actions for dismissing an error occurred during cart operations
 */
export const DISMISS_ERROR = 'CART/DISMISS_ERROR'

export const dismissError = () => ({
  type: DISMISS_ERROR
})
